import React, { PropsWithChildren, useContext } from 'react';
import { Delay } from 'src/components/Helpers/Delay';
import { AggregationSelections } from 'src/components/CategoryListingPage/utils/cleanAggregations';
import { FilterModalContext } from 'src/components/CategoryListingPage/FilterModalContext';
import { SkeletonBlock } from 'src/components/Helpers/SkeletonBlock';
import { BehaviorSubject } from 'rxjs';
import { MappedAggregations } from 'src/components/CategoryListingPage/utils/cleanAggregations';
import classes from 'src/components/LayeredNav/LayeredNav.scss';
import { LayeredNavQueryContextType } from 'src/components/LayeredNav/LayeredNavQueryProvider';
import classnames from 'classnames';

import { CurrentSelectionsProps } from './CurrentSelection';
import { LayeredNavLayout } from './LayeredNavLayout';
import { AggregationListProps } from './AggregationList';

export const EMPTY_ARR = [];

type LayeredNavProps = {
    ref: React.MutableRefObject<HTMLDivElement | null>;
    onRemove: (code: string, value: string, label: string) => void;
    onRemoveAll: () => void;
    onChange: (checked: boolean, code: string, label: string) => void;
    selection$: BehaviorSubject<AggregationSelections>;
    mappedAggregations: MappedAggregations;
    isMob: boolean;
    filtersSelected?: boolean;
    parsedCategoryProduct: LayeredNavQueryContextType;
    urlKey?: string;
    modalMode?: boolean;
};

export const LayeredNav: React.FC<LayeredNavProps> = ({
    ref,
    onRemove,
    onRemoveAll,
    onChange,
    selection$,
    mappedAggregations,
    isMob,
    filtersSelected,
    parsedCategoryProduct,
    urlKey,
    modalMode = false,
}) => {
    return (
        <div ref={ref}>
            <LayeredNavLayout
                current={
                    <LazySelections
                        mappedAggregations={mappedAggregations}
                        selection$={selection$}
                        onRemoveAll={onRemoveAll}
                        onRemove={onRemove}
                        isMob={isMob}
                    />
                }
                selections={
                    <LazyItems
                        selection$={selection$}
                        isMob={isMob}
                        onChange={onChange}
                        modeModal={true}
                        mappedAggregations={mappedAggregations}
                        parsedCategoryProduct={parsedCategoryProduct}
                        urlKey={urlKey}
                        onRemove={onRemove}
                    />
                }
                onRemoveAll={onRemoveAll}
                filtersSelected={filtersSelected}
            />
            <div
                className={classnames({
                    [classes.options]: modalMode,
                })}
            >
                <div className={classes.filterTitle}>
                    <p className={classes.filterFilter}>Filter Products</p>
                    {filtersSelected && (
                        <span className={classes.filterClear} onClick={onRemoveAll}>
                            Clear filters
                        </span>
                    )}
                </div>
                <div>
                    <LazySelections
                        mappedAggregations={mappedAggregations}
                        selection$={selection$}
                        onRemoveAll={onRemoveAll}
                        onRemove={onRemove}
                        isMob={isMob}
                    />
                    <LazyItems
                        selection$={selection$}
                        onRemove={onRemove}
                        isMob={isMob}
                        onChange={onChange}
                        modeModal={false}
                        mappedAggregations={mappedAggregations}
                        parsedCategoryProduct={parsedCategoryProduct}
                        urlKey={urlKey}
                    />
                </div>
            </div>
        </div>
    );
};

function LazySelections(props: PropsWithChildren<CurrentSelectionsProps & { isMob: boolean }>) {
    const { showFilter } = useContext(FilterModalContext);
    const output = (
        <LazyCurrentSelection
            mappedAggregations={props.mappedAggregations}
            selection$={props.selection$}
            onRemove={props.onRemove}
            onRemoveAll={props.onRemoveAll}
        />
    );

    if (!props.isMob) {
        return (
            <Delay delay={1}>
                <React.Suspense fallback={null}>{output}</React.Suspense>
            </Delay>
        );
    }

    if (props.isMob) {
        if (showFilter) {
            return (
                <Delay delay={300}>
                    <React.Suspense fallback={null}>{output}</React.Suspense>
                </Delay>
            );
        }
    }

    return null;
}

type LazyItemsProps = {
    selection$: BehaviorSubject<AggregationSelections>;
    onChange: (checked: boolean, code: string, label: string) => void;
    isMob: boolean;
    modeModal: boolean;
    mappedAggregations: MappedAggregations;
    parsedCategoryProduct: LayeredNavQueryContextType;
    urlKey?: string;
    onRemove: (code: string, value: string, label: string) => void;
};

function LazyItems(props: PropsWithChildren<LazyItemsProps>) {
    const { showFilter } = useContext(FilterModalContext);

    const output = (
        <Delay minHeight="100vh" delay={props.isMob ? 300 : 1}>
            <React.Suspense fallback={LINES}>
                <LazyAggregationList
                    selection$={props.selection$}
                    onChange={props.onChange}
                    isMob={props.isMob}
                    modeModal={props.modeModal}
                    mappedAggregations={props.mappedAggregations}
                    parsedCategoryProduct={props.parsedCategoryProduct}
                    urlKey={props.urlKey}
                    onRemove={props.onRemove}
                />
            </React.Suspense>
        </Delay>
    );

    /**
     * on desktop, just continue as normal
     */
    if (!props.isMob) {
        return (
            <Delay delay={1}>
                <React.Suspense fallback={null}>
                    <LazyAggregationList
                        selection$={props.selection$}
                        onChange={props.onChange}
                        isMob={props.isMob}
                        modeModal={props.modeModal}
                        mappedAggregations={props.mappedAggregations}
                        parsedCategoryProduct={props.parsedCategoryProduct}
                        urlKey={props.urlKey}
                        onRemove={props.onRemove}
                    />
                </React.Suspense>
            </Delay>
        );
    }

    /**
     * If we're on mobile, only load if the modal is open
     */
    if (props.isMob) {
        if (showFilter) {
            return output;
        }
    }

    return null;
}

const LazyAggregationList: React.FC<AggregationListProps> = React.lazy(
    () => import(/* webpackChunkName:"__lazy__AggregationList" */ './AggregationList'),
);

const LazyCurrentSelection: React.FC<CurrentSelectionsProps> = React.lazy(
    () => import(/* webpackChunkName:"__lazy__CurrentSelection" */ './CurrentSelection'),
);

const LINE = <SkeletonBlock style={{ width: '100%', height: '25px', marginBottom: '20px' }} />;
export const LINES = (
    <div>
        {LINE}
        {LINE}
        {LINE}
    </div>
);
